import _gql from 'graphql-tag';
import { get, includes, isArray, isFinite } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { useRouteMatch } from 'react-router-dom';
import getGqlOperationName from '../../../../utils/getGqlOperationName';
import Breadcrumbs from '../../../utils/Breadcrumbs';
import ContentBox from '../../../utils/ContentBox';
import Empty from '../../../utils/Empty';
import FormTitle from '../../../utils/FormTitle';
import SpinnerError from '../../../utils/SpinnerError';
import TabsSaver from '../../../utils/Tabs/TabsSaver';
import useTranslation from '../../../utils/Translations/useTranslation';
import { ROOT_NODE_ID } from '../../DynamicTree';
const GqlFullCrudTreeForm = ({ component: Component = () => null, gql = {}, nodeId, isNew = false, title = '', hasTitle = true, refetchQueries, selectedNode, selectedNodeMeta, setSelectedNode, onSelectionChange, filter = null, lastFilter = null, resolveTitle = null, resolveTitleFromSelected = null, skipEmpty = false, setNodeExpanded, ...rest }) => {
    const { single, getParentGql, singleVariables, create, update, fetchPolicy, } = gql;
    const { t } = useTranslation();
    const { url } = useRouteMatch();
    const query = useMemo(() => isNew && getParentGql && selectedNode
        ? getParentGql(selectedNode)
        : single, [getParentGql, isNew, selectedNode, single]);
    const action = useMemo(() => (isNew ? create : update), [
        create,
        isNew,
        update,
    ]);
    const queryRes = useQuery(query, {
        skip: !isFinite(nodeId),
        variables: { id: nodeId, ...singleVariables },
        ...(fetchPolicy ? { fetchPolicy } : {}),
    });
    const refetchSingle = useMemo(() => ({
        query,
        variables: { id: nodeId, ...singleVariables },
    }), [nodeId, query, singleVariables]);
    const node = useMemo(() => get(queryRes, `data.${getGqlOperationName(query)}`, {}), [query, queryRes]);
    const [mutation, mutationRes] = useMutation(action || _gql `mutation m{m}`, {
        refetchQueries,
        awaitRefetchQueries: true,
    });
    useEffect(() => {
        if (node && isFinite(node.id)) {
            setSelectedNode(node);
        }
        else if (!isFinite(nodeId) && isNew) {
            setSelectedNode({ id: ROOT_NODE_ID });
        }
    }, [isNew, node, nodeId, setSelectedNode]);
    const onSubmit = useCallback(async (params) => {
        const res = await mutation({
            variables: isNew ? { params } : { id: params.id, params },
        });
        const node = get(res, `data.${getGqlOperationName(action)}`);
        if (node && isFinite(node.id)) {
            if (!lastFilter ||
                !node.status ||
                (isArray(lastFilter.status)
                    ? includes(lastFilter.status, node.status)
                    : lastFilter.status === node.status)) {
                onSelectionChange(node);
                setNodeExpanded(node);
            }
            else {
                onSelectionChange(null);
            }
        }
    }, [action, lastFilter, isNew, mutation, onSelectionChange, setNodeExpanded]);
    const _title = useMemo(() => {
        if (isNew) {
            return t('Add #{name}', { name: title });
        }
        if (resolveTitle) {
            return resolveTitle(node);
        }
        if (resolveTitleFromSelected && selectedNode) {
            return resolveTitleFromSelected(selectedNode);
        }
        return node.name;
    }, [
        resolveTitleFromSelected,
        resolveTitle,
        isNew,
        node,
        t,
        title,
        selectedNode,
    ]);
    const _error = useMemo(() => queryRes.error, [queryRes.error]);
    const _loading = useMemo(() => queryRes.loading || mutationRes.loading, [
        mutationRes.loading,
        queryRes.loading,
    ]);
    return (<TabsSaver>
      <SpinnerError error={_error} firstLoading={queryRes.loading} inline={false} loading={_loading}>
        <Breadcrumbs.Anchor route={url} title={_title}>
          {!isNew && !isFinite(node.id) && !skipEmpty ? (<Empty />) : (<ContentBox className="right-panel disableScrollbar">
              {hasTitle && <FormTitle title={_title}/>}
              <Component node={isNew ? undefined : node} parentNode={isNew ? node : undefined} refetchSingle={refetchSingle} selectedNode={selectedNode} selectedNodeMeta={selectedNodeMeta} title={title} onSubmit={onSubmit} {...rest}/>
            </ContentBox>)}
        </Breadcrumbs.Anchor>
      </SpinnerError>
    </TabsSaver>);
};
export default GqlFullCrudTreeForm;
